import { type FormEvent, type ReactElement, useCallback, useEffect, useState } from 'react';
import { type AccessProfile, type ApiError, type IssuedOperatorInvitation, type ManagedOperator, type OperatorActionReason, type OperatorGrantRequest } from '@vello/api-client';
import {
  GRANTABLE_ROLES, GRANT_SCOPE_TYPES, OPERATOR_ACTION_REASON_CODES, canDecideGrantRequest, maySelfApprove, emptyGrantForm, emptyInviteForm, formatWireMoney, grantRegionChoices, inviteRealmChoices, grantRegionCountries,
  isFourEyesAdminRole, normalizeGrantForm, normalizeInviteForm, operatorRowActions,
  type GrantForm, type GrantScopeType, type InviteForm, type InviteRealm, type OperatorActionReasonCode, type OperatorFormIssue,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useScopeName } from '../i18n/scope-name';
import { useCountryName } from '../i18n/country-name';
import { Badge, Button, EmptyResult, Field, Notice, PageHeader, Select, TextInput, Cluster, Stack, Text, Card, CardForm, Drawer, DrawerHeader, DrawerBody, DrawerSection, DrawerFooter, KeyValueList, ListTable, Ink, type Column } from '@vello/web-theme';
import { IdRef } from '../components/code-ref';
import { DrawerStack } from '../components/drawer-stack';

type Msg = { tone: 'success' | 'danger' | 'warning'; text: string; link?: string };
type LifecycleAction = 'deactivate' | 'reactivate' | 'revokeSessions' | 'resetMfa' | 'reissue';

/**
 * Configurações › **Operadores** (W8-OPERATORS-AND-SCOPES, D449, ADR-0078; finding A-05). The staff directory an admin works:
 * invite an operator (the link goes by e-mail, or — with no e-mail integration — is shown ONCE to hand over in person), grant
 * a role at a platform, country or city scope with an optional financial limit, revoke it, deactivate or reactivate the
 * account, end its sessions, reset its second factor, and decide the admin roles another operator proposed. An admin role
 * is never one person's act: granting one records a request that a DIFFERENT operator approves here.
 *
 * Every write asks for a reason from the closed vocabulary. The server authorizes each call at the operator's country or
 * the grant's scope and refuses every self-action; this screen hides what it can foresee and explains the rest.
 */
export function OperatorsScreen(): ReactElement {
  const { runtime, me, regions: stateCatalog, grants: myGrants } = useAdmin();
  const selfApproval = maySelfApprove(myGrants); // D868: a GLOBAL super administrator decides their own proposals
  const realmChoices = inviteRealmChoices(myGrants); // D849 (P0-009): the store login is offered only to who may put people on a store's team
  const countryName = useCountryName();
  const regions = stateCatalog ?? [];
  const regionCountries = grantRegionCountries(regions);
  // The state select starts on the operator's own country when it has states, else on the first country that does.
  const ownCountry = me?.countryCode?.toUpperCase();
  const defaultRegionCountry = ownCountry !== undefined && regionCountries.includes(ownCountry) ? ownCountry : (regionCountries[0] ?? '');
  const { confirm } = useConsole();
  const { t, lang } = useT();
  const { v, fail } = useVocab();
  const scopeLabel = useScopeName();
  const api = runtime.operatorsAdmin;

  const [operators, setOperators] = useState<readonly ManagedOperator[] | undefined>(undefined);
  const [queue, setQueue] = useState<readonly OperatorGrantRequest[] | undefined>(undefined);
  const [msg, setMsg] = useState<Msg | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [invite, setInvite] = useState<InviteForm>(() => emptyInviteForm(me?.countryCode ?? '', me?.locale ?? '', me?.timezone ?? ''));
  const [inviteReason, setInviteReason] = useState<OperatorActionReasonCode>('operator_onboarding');
  const [issues, setIssues] = useState<readonly OperatorFormIssue[]>([]);
  const [openId, setOpenId] = useState<string | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [grant, setGrant] = useState<GrantForm>(() => emptyGrantForm());
  const [grantReason, setGrantReason] = useState<OperatorActionReasonCode>('operator_role_change');
  // D475 (A-018): the custom access profiles a grant may name (an operator without `access_profile:manage` simply sees none).
  const [profiles, setProfiles] = useState<readonly AccessProfile[]>([]);

  const refusal = useCallback((e: ApiError): string => (e.code === 'FORBIDDEN' ? t('opr.denied') : t('opr.failed', { code: fail(e) })), [t, fail]);

  const load = useCallback(async (): Promise<void> => {
    const [list, requests] = await Promise.all([api.list(), api.grantRequests()]);
    if (list.ok) setOperators(list.value.operators);
    else {
      setOperators([]);
      setMsg({ tone: 'danger', text: refusal(list.error) });
    }
    setQueue(requests.ok ? requests.value.requests : []);
    const named = await runtime.accessProfiles.list();
    setProfiles(named.ok ? named.value.profiles.filter((p) => p.live !== undefined) : []);
  }, [api, refusal, runtime.accessProfiles]);

  useEffect(() => {
    void load();
  }, [load]);

  const reasonLabels = OPERATOR_ACTION_REASON_CODES.map((c) => v('operator.action_reason', c));
  const askReason = async (title: string, danger: boolean): Promise<OperatorActionReason | undefined> => {
    const answer = await confirm({
      title, body: t('opr.confirm.body'), confirmLabel: title, cancelLabel: t('opr.cancel'), tone: danger ? 'danger' : 'primary', icon: 'shieldAlert',
      reasons: reasonLabels, reasonLabel: t('opr.field.reason'),
    });
    if (!answer.confirmed || answer.reason === undefined) return undefined;
    const code = OPERATOR_ACTION_REASON_CODES[reasonLabels.indexOf(answer.reason)];
    return code === undefined ? undefined : { reasonCode: code, ...(answer.note !== undefined ? { note: answer.note } : {}) };
  };

  /** D849 (P0-011): word what the API says happened to the invitation - never "sent" for a link the inviter must hand over. */
  const invitationNotice = (inv: IssuedOperatorInvitation, email: string): { tone: 'success' | 'warning'; text: string; link?: string } => {
    if (inv.delivery === 'EMAIL') return { tone: 'success', text: t('opr.invited.email', { email }) };
    const id = inv.reason === 'EMAIL_OFF' ? 'opr.invited.manual.off' : inv.reason === 'EMAIL_FAILED' ? 'opr.invited.manual.failed' : 'opr.invited.manual';
    return { tone: 'warning', text: t(id, { email }), link: inv.url };
  };

  const onInvite = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMsg(undefined);
    const normalized = normalizeInviteForm(invite, inviteReason);
    if (!normalized.ok) {
      setIssues(normalized.error);
      return;
    }
    setIssues([]);
    setBusy(true);
    const res = await api.invite(normalized.value);
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: refusal(res.error) });
      return;
    }
    const inv = res.value.invitation;
    setMsg(invitationNotice(inv, normalized.value.email));
    setInviting(false);
    setInvite(emptyInviteForm(invite.countryCode, invite.locale, invite.timezone));
    await load();
  };

  const onGrant = async (op: ManagedOperator): Promise<void> => {
    setMsg(undefined);
    const normalized = normalizeGrantForm(grant, grantReason, stateCatalog);
    if (!normalized.ok) {
      setIssues(normalized.error);
      return;
    }
    setIssues([]);
    setBusy(true);
    const res = await api.grant(op.userId, normalized.value);
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: refusal(res.error) });
      return;
    }
    const scope = scopeLabel(normalized.value);
    setMsg(res.value.fourEyes ? { tone: 'warning', text: t('opr.grant.requested', { role: v('rbac.role', normalized.value.role), scope }) } : { tone: 'success', text: t('opr.grant.added', { role: v('rbac.role', normalized.value.role), scope }) });
    setGrant(emptyGrantForm());
    await load();
  };

  const onRevoke = async (op: ManagedOperator, grantId: string, role: string): Promise<void> => {
    const reason = await askReason(t('opr.confirm.title', { action: t('opr.grant.revoke'), who: `${op.displayName ?? op.identifier} · ${v('rbac.role', role)}` }), true);
    if (reason === undefined) return;
    const res = await api.revokeGrant(op.userId, grantId, reason);
    setMsg(res.ok ? { tone: 'success', text: t('opr.grant.revoked') } : { tone: 'danger', text: refusal(res.error) });
    if (res.ok) await load();
  };

  const LIFECYCLE_LABEL: Readonly<Record<LifecycleAction, string>> = {
    deactivate: 'opr.action.deactivate', reactivate: 'opr.action.reactivate', revokeSessions: 'opr.action.revokeSessions', resetMfa: 'opr.action.resetMfa', reissue: 'opr.action.reissue',
  };
  const onLifecycle = async (op: ManagedOperator, action: LifecycleAction): Promise<void> => {
    const reason = await askReason(t('opr.confirm.title', { action: t(LIFECYCLE_LABEL[action]), who: op.displayName ?? op.identifier }), action !== 'reactivate' && action !== 'reissue');
    if (reason === undefined) return;
    setMsg(undefined);
    if (action === 'resetMfa' || action === 'reissue') {
      const res = action === 'resetMfa' ? await api.resetMfa(op.userId, reason) : await api.reissueInvitation(op.userId, reason);
      if (!res.ok) setMsg({ tone: 'danger', text: refusal(res.error) });
      else {
        const inv = res.value.invitation;
        setMsg(invitationNotice(inv, op.identifier));
      }
    } else {
      const res = action === 'deactivate' ? await api.deactivate(op.userId, reason) : action === 'reactivate' ? await api.reactivate(op.userId, reason) : await api.revokeSessions(op.userId, reason);
      if (!res.ok) setMsg({ tone: 'danger', text: refusal(res.error) });
      else setMsg({ tone: 'success', text: 'sessionsRevoked' in res.value ? t('opr.sessionsEnded', { n: res.value.sessionsRevoked }) : t('opr.done') });
    }
    await load();
  };

  const onDecide = async (r: OperatorGrantRequest, action: 'approve' | 'reject' | 'withdraw'): Promise<void> => {
    let note: string | undefined;
    if (action === 'reject') {
      const answer = await confirm({ title: t('opr.queue.reject'), body: t('opr.queue.rejectNote'), confirmLabel: t('opr.queue.reject'), cancelLabel: t('opr.cancel'), tone: 'danger', notePlaceholder: t('opr.queue.rejectNote') });
      if (!answer.confirmed || answer.note === undefined || answer.note.trim() === '') return;
      note = answer.note;
    }
    const res = await api.decideGrantRequest(r.id, action, note);
    setMsg(res.ok ? { tone: 'success', text: t('opr.done') } : { tone: 'danger', text: refusal(res.error) });
    if (res.ok) await load();
  };

  const issueText = (field: OperatorFormIssue): string | undefined => (issues.includes(field) ? t(`opr.issue.${field}`) : undefined);
  const setI = (k: keyof InviteForm) => (val: string): void => setInvite((f) => ({ ...f, [k]: val }));
  const reasonSelect = (id: string, value: OperatorActionReasonCode, onChange: (c: OperatorActionReasonCode) => void): ReactElement => (
    <Field label={t('opr.field.reason')} htmlFor={id}>
      <Select id={id} value={value} onChange={(e) => onChange(e.target.value as OperatorActionReasonCode)}>
        {OPERATOR_ACTION_REASON_CODES.map((c) => <option key={c} value={c}>{v('operator.action_reason', c)}</option>)}
      </Select>
    </Field>
  );

  const notice = msg !== undefined && (
    <Notice tone={msg.tone}>
      {msg.text}
      {msg.link !== undefined && <Text as="code" mono block data-testid="opr-manual-link">{msg.link}</Text>}
    </Notice>
  );
  const roleText = (g: ManagedOperator['grants'][number]): string => `${v('rbac.role', g.role)}${g.profileId !== undefined ? ` (${profiles.find((p) => p.id === g.profileId)?.name ?? '…'})` : ''}`;
  const nameOf = (op: ManagedOperator): string => op.displayName ?? op.identifier;
  const statusTone = (op: ManagedOperator): 'success' | 'warning' | 'neutral' => (op.status === 'ACTIVE' ? 'success' : op.status === 'INVITED' ? 'warning' : 'neutral');
  const mfaTone = (op: ManagedOperator): 'success' | 'warning' | 'neutral' => (op.mfa === 'SEALED' ? 'success' : op.mfa === 'SHARED_LEGACY' ? 'warning' : 'neutral');
  const columns: readonly Column<ManagedOperator>[] = [
    { key: 'operator', header: t('opr.col.operator'), width: '1.4fr', render: (op) => <Ink strong truncate>{nameOf(op)}</Ink> },
    { key: 'email', header: t('opr.col.email'), width: '1.6fr', render: (op) => <Ink tone="secondary" truncate>{op.identifier}</Ink> },
    { key: 'roles', header: t('opr.col.roles'), width: '1.8fr', render: (op) => <Ink tone="secondary">{op.grants.length === 0 ? t('opr.grants.none') : op.grants.map(roleText).join(' · ')}</Ink> },
    { key: 'scope', header: t('opr.col.scope'), width: '1.4fr', render: (op) => <Ink tone="secondary">{op.grants.length === 0 ? '—' : [...new Set(op.grants.map((g) => scopeLabel(g)))].join(' · ')}</Ink> },
    { key: 'status', header: t('opr.col.status'), width: '.9fr', render: (op) => <Badge tone={statusTone(op)}>{v('operator.status', op.status)}</Badge> },
    { key: 'mfa', header: t('opr.col.mfa'), width: '.9fr', render: (op) => <Badge tone={mfaTone(op)}>{v('operator.mfa', op.mfa)}</Badge> },
  ];
  const queueColumns: readonly Column<OperatorGrantRequest>[] = [
    { key: 'who', header: t('opr.col.operator'), width: '1.4fr', render: (r) => <Ink strong truncate>{r.operator?.displayName ?? r.operator?.identifier ?? r.userId}</Ink> },
    { key: 'role', header: t('opr.field.role'), width: '1.4fr', render: (r) => <Ink>{v('rbac.role', r.role)}</Ink> },
    { key: 'scope', header: t('opr.col.scope'), width: '1.2fr', render: (r) => <Ink tone="secondary">{scopeLabel(r)}</Ink> },
    { key: 'reason', header: t('opr.field.reason'), width: '1.6fr', render: (r) => <Ink tone="secondary">{v('operator.action_reason', r.reasonCode)}{r.note !== undefined ? ` · ${r.note}` : ''}{r.mine === true && !canDecideGrantRequest(r, selfApproval, me?.id) ? ` · ${t('opr.queue.mine')}` : ''}</Ink> },
  ];
  const openOperator = (op: ManagedOperator): void => {
    setOpenId(op.userId);
    setDrawerOpen(true);
    setIssues([]);
    setGrant(emptyGrantForm());
  };
  const drawerOp = operators?.find((o) => o.userId === openId);

  const renderDrawer = (op: ManagedOperator): ReactElement => {
    const can = operatorRowActions(op, me?.id);
    return (
      <>
        <DrawerHeader titleId="opr-drawer-title" title={nameOf(op)} subtitle={op.identifier} actions={<Badge tone={statusTone(op)}>{v('operator.status', op.status)}</Badge>} onClose={() => setDrawerOpen(false)} closeLabel={t('ui.close')} />
        <DrawerBody>
          <DrawerStack>
            {notice}
            <KeyValueList
              items={[
                { label: t('opr.col.realm'), value: op.realm === 'MERCHANT' || op.realm === 'BUSINESS' ? t(`opr.realm.${op.realm}`) : op.realm },
                { label: t('opr.col.country'), value: op.countryCode, mono: true },
                { label: t('opr.col.mfa'), value: <Badge tone={mfaTone(op)}>{v('operator.mfa', op.mfa)}</Badge> },
                { label: t('opr.col.recovery'), value: t('opr.recoveryLeft', { n: op.recoveryCodesRemaining }) },
                { label: t('opr.col.id'), value: <IdRef value={op.userId} copy full />, mono: true },
              ]}
            />
            <DrawerSection title={t('opr.grants')}>
              <Stack gap={2}>
                {op.grants.length === 0 && <Text role="caption" tone="secondary" block>{t('opr.grants.none')}</Text>}
                {op.grants.map((g) => (
                  <Cluster key={g.id} gap={2} wrap justify="between" align="center">
                    <span>
                      {roleText(g)} · {scopeLabel(g)}
                      {g.financialLimitMinor !== undefined && g.financialLimitCurrency !== undefined && (
                        <Text role="caption" tone="secondary" block>{t('opr.limit', { amount: formatWireMoney({ amountMinor: g.financialLimitMinor, currency: g.financialLimitCurrency }, lang) })}</Text>
                      )}
                    </span>
                    {!can.reactivate && <Button variant="ghost" size="sm" onClick={() => void onRevoke(op, g.id, g.role)} data-testid={`opr-revoke-${g.id}`}>{t('opr.grant.revoke')}</Button>}
                  </Cluster>
                ))}
              </Stack>
            </DrawerSection>
            {op.status !== 'DEACTIVATED' && (
              <DrawerSection title={t('opr.grant.add')}>
                <Stack gap={2} data-testid="opr-grant-form">
                  <Field label={t('opr.field.role')} htmlFor="opr-role" hint={issueText('role') ?? issueText('scopeNotAllowed') ?? (isFourEyesAdminRole(grant.role) ? t('opr.grant.fourEyesHint') : undefined)}>
                    <Select id="opr-role" value={grant.role} onChange={(e) => setGrant((g) => ({ ...g, role: e.target.value, profileId: '' }))}>
                      <option value="">—</option>
                      {GRANTABLE_ROLES.map((r) => <option key={r} value={r}>{v('rbac.role', r)}</option>)}
                    </Select>
                  </Field>
                  {profiles.some((p) => p.baseRole === grant.role) && (
                    <Field label={t('opr.field.profile')} htmlFor="opr-profile" hint={t('opr.field.profile.hint')}>
                      <Select id="opr-profile" value={grant.profileId ?? ''} onChange={(e) => setGrant((g) => ({ ...g, profileId: e.target.value }))}>
                        <option value="">{t('opr.field.profile.none')}</option>
                        {profiles.filter((p) => p.baseRole === grant.role).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </Select>
                    </Field>
                  )}
                  <Field label={t('opr.field.scopeType')} htmlFor="opr-scope-type">
                    <Select id="opr-scope-type" value={grant.scopeType} onChange={(e) => setGrant((g) => ({ ...g, scopeType: e.target.value as GrantScopeType, scopeId: '' }))}>
                      {GRANT_SCOPE_TYPES.map((s) => <option key={s} value={s}>{v('rbac.scope_type', s)}</option>)}
                    </Select>
                  </Field>
                  {grant.scopeType === 'REGION' && (
                    <>
                      <Field label={t('opr.field.regionCountry')} htmlFor="opr-region-country">
                        <Select id="opr-region-country" value={grant.regionCountry ?? defaultRegionCountry} disabled={regionCountries.length === 0} onChange={(e) => setGrant((g) => ({ ...g, regionCountry: e.target.value, scopeId: '' }))}>
                          {regionCountries.map((cc) => <option key={cc} value={cc}>{countryName(cc)}</option>)}
                        </Select>
                      </Field>
                      <Field label={t('opr.field.regionState')} htmlFor="opr-region-state" hint={issues.includes('scopeId') ? t('opr.issue.region') : regionCountries.length === 0 ? t('opr.field.regionState.empty') : undefined}>
                        <Select id="opr-region-state" value={grant.scopeId} disabled={regionCountries.length === 0} onChange={(e) => setGrant((g) => ({ ...g, scopeId: e.target.value }))}>
                          <option value="">—</option>
                          {grantRegionChoices(regions, grant.regionCountry ?? defaultRegionCountry).map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
                        </Select>
                      </Field>
                    </>
                  )}
                  {grant.scopeType !== 'GLOBAL' && grant.scopeType !== 'REGION' && (
                    <Field label={t('opr.field.scopeId')} htmlFor="opr-scope-id" hint={issueText('scopeId')}><TextInput id="opr-scope-id" value={grant.scopeId} onChange={(e) => setGrant((g) => ({ ...g, scopeId: e.target.value }))} /></Field>
                  )}
                  <Field label={t('opr.field.limit')} htmlFor="opr-limit" hint={issueText('limit')}><TextInput id="opr-limit" inputMode="numeric" className="tnum" value={grant.limitMinor} onChange={(e) => setGrant((g) => ({ ...g, limitMinor: e.target.value }))} /></Field>
                  {grant.limitMinor.trim() !== '' && (
                    <Field label={t('opr.field.currency')} htmlFor="opr-currency" hint={issueText('currency')}><TextInput id="opr-currency" value={grant.limitCurrency} onChange={(e) => setGrant((g) => ({ ...g, limitCurrency: e.target.value }))} /></Field>
                  )}
                  {reasonSelect('opr-grant-reason', grantReason, setGrantReason)}
                  <Cluster gap={2} wrap><Button variant="secondary" disabled={busy} onClick={() => void onGrant(op)} data-testid="opr-grant-send">{t('opr.grant.add')}</Button></Cluster>
                </Stack>
              </DrawerSection>
            )}
          </DrawerStack>
        </DrawerBody>
        <DrawerFooter>
          {can.reissueInvitation && <Button variant="ghost" onClick={() => void onLifecycle(op, 'reissue')}>{t('opr.action.reissue')}</Button>}
          {can.revokeSessions && <Button variant="ghost" onClick={() => void onLifecycle(op, 'revokeSessions')} data-testid={`opr-sessions-${op.userId}`}>{t('opr.action.revokeSessions')}</Button>}
          {can.resetMfa && <Button variant="ghost" onClick={() => void onLifecycle(op, 'resetMfa')}>{t('opr.action.resetMfa')}</Button>}
          {can.deactivate && <Button variant="danger" onClick={() => void onLifecycle(op, 'deactivate')} data-testid={`opr-deactivate-${op.userId}`}>{t('opr.action.deactivate')}</Button>}
          {can.reactivate && <Button variant="ghost" onClick={() => void onLifecycle(op, 'reactivate')}>{t('opr.action.reactivate')}</Button>}
        </DrawerFooter>
      </>
    );
  };

  return (
    <div>
      <PageHeader
        title={t('opr.title')}
        subtitle={t('opr.subtitle')}
        actions={<Button variant="primary" onClick={() => { setInviting(true); setIssues([]); }} data-testid="opr-invite-open">{t('opr.invite')}</Button>}
      />
      {!drawerOpen && notice}

      {inviting && (
        <CardForm onSubmit={(e) => void onInvite(e)} noValidate aria-label={t('opr.invite')} data-testid="opr-invite-form">
          <Stack gap={4}>
            {realmChoices.length > 1 && (
              <Field label={t('opr.field.realm')} htmlFor="opr-realm" hint={t(`opr.realm.hint.${invite.realm ?? 'BUSINESS'}`)}>
                <Select id="opr-realm" value={invite.realm ?? 'BUSINESS'} onChange={(e) => setInvite((f) => ({ ...f, realm: e.target.value as InviteRealm }))}>
                  {realmChoices.map((r) => <option key={r} value={r}>{t(`opr.realm.${r}`)}</option>)}
                </Select>
              </Field>
            )}
            <Field label={t('opr.field.email')} htmlFor="opr-email" hint={issueText('email')}><TextInput id="opr-email" type="email" autoComplete="off" value={invite.email} onChange={(e) => setI('email')(e.target.value)} /></Field>
            <Field label={t('opr.field.name')} htmlFor="opr-name" hint={issueText('displayName')}><TextInput id="opr-name" value={invite.displayName} onChange={(e) => setI('displayName')(e.target.value)} /></Field>
            <Field label={t('opr.field.phone')} htmlFor="opr-phone" hint={issueText('phone')}><TextInput id="opr-phone" type="tel" className="tnum" value={invite.phoneE164} onChange={(e) => setI('phoneE164')(e.target.value)} /></Field>
            <Field label={t('opr.field.country')} htmlFor="opr-country" hint={issueText('country')}><TextInput id="opr-country" value={invite.countryCode} onChange={(e) => setI('countryCode')(e.target.value)} /></Field>
            <Field label={t('opr.field.locale')} htmlFor="opr-locale" hint={issueText('locale')}><TextInput id="opr-locale" value={invite.locale} onChange={(e) => setI('locale')(e.target.value)} /></Field>
            <Field label={t('opr.field.timezone')} htmlFor="opr-tz" hint={issueText('timezone')}><TextInput id="opr-tz" value={invite.timezone} onChange={(e) => setI('timezone')(e.target.value)} /></Field>
            {reasonSelect('opr-invite-reason', inviteReason, setInviteReason)}
            <Cluster gap={2} wrap>
              <Button type="submit" variant="secondary" disabled={busy} data-testid="opr-invite-send">{t('opr.invite.send')}</Button>
              <Button variant="ghost" onClick={() => setInviting(false)}>{t('opr.cancel')}</Button>
            </Cluster>
          </Stack>
        </CardForm>
      )}

      {queue !== undefined && queue.length > 0 && (
        <Card as="section" aria-label={t('opr.queue.title')} data-testid="opr-queue">
          <Stack gap={3}>
            <strong>{t('opr.queue.title')}</strong>
            <ListTable
              columns={queueColumns}
              rows={queue}
              rowKey={(r) => r.id}
              minWidth={720}
              actionsWidth="max-content"
              countLabel={t('opr.queue.count', { n: String(queue.length) })}
              actions={(r) => (
                <span data-testid={`opr-request-${r.id}`}>
                  <Cluster gap={2} wrap>
                    {canDecideGrantRequest(r, selfApproval, me?.id) && <Button variant="secondary" size="sm" onClick={() => void onDecide(r, 'approve')} data-testid={`opr-approve-${r.id}`}>{t('opr.queue.approve')}</Button>}
                    {canDecideGrantRequest(r) && <Button variant="ghost" size="sm" onClick={() => void onDecide(r, 'reject')}>{t('opr.queue.reject')}</Button>}
                    {r.mine === true && <Button variant="ghost" size="sm" onClick={() => void onDecide(r, 'withdraw')}>{t('opr.queue.withdraw')}</Button>}
                  </Cluster>
                </span>
              )}
            />
          </Stack>
        </Card>
      )}

      {operators !== undefined && operators.length === 0 && msg?.tone !== 'danger' && <EmptyResult title={t('opr.empty.title')} detail={t('opr.empty.detail')} />}
      {operators !== undefined && operators.length > 0 && (
        <ListTable
          columns={columns}
          rows={operators}
          rowKey={(op) => op.userId}
          minWidth={1000}
          onRowClick={openOperator}
          selectedKey={drawerOpen ? openId : undefined}
          countLabel={t('opr.count', { n: String(operators.length) })}
        />
      )}

      <Drawer open={drawerOpen && drawerOp !== undefined} onClose={() => setDrawerOpen(false)} labelledBy="opr-drawer-title">
        {drawerOp !== undefined && renderDrawer(drawerOp)}
      </Drawer>
    </div>
  );
}
